import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { ProjectListItem } from "@/api/projects.api";
import { computed, defineComponent } from "vue";
import { RouterLink } from "vue-router";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import Icon from "@/components/ui/icon/Icon";
import Tooltip from "@/components/ui/tooltip/Tooltip";
import { resolveProjectIcon } from "@/pages/projects/project-appearance";
import { $t } from "@/services/i18n";
import { useProjectStore } from "@/stores/project.store";
import { p } from "@/utils/props-builder";
import { formatId } from "@/utils/uuid";

const props = {
  projectId: p.string(),
  /** Resource type for reassign functionality. If not provided, reassign won't be available */
  resourceType: p.string<
    "agent" | "webhook" | "api_key" | "knowledge" | "structured_analysis" | "thread"
  >(),
  /** Resource ID for reassign functionality */
  resourceId: p.string(),
  /** Callback when project is reassigned */
  onReassign: p.func<(projectId: string) => Promise<void>>(),
  /** Force show regardless of isGlobalView (e.g. for org analytics) */
  forceShow: p.boolean(),
} satisfies ComponentPropsOptions;

type Props = ExtractPublicPropTypes<typeof props>;

/**
 * ProjectColumn - Displays project information as a table column
 *
 * AI-196: Replaces ProjectBadge with a proper column that shows:
 * - Project name (clickable link) for existing projects
 * - ID truncated + "Removed" badge for deleted projects
 * - "Orphan" badge + reassign button for null project_id
 *
 * Only visible in "All Projects" (global) view since in project-specific
 * views all resources belong to the same project.
 *
 * @example
 * ```vue
 * <ProjectColumn
 *   projectId={agent.project_id}
 *   resourceType="agent"
 *   resourceId={agent.id}
 *   onReassign={(projectId) => reassignAgent(agent.id, projectId)}
 * />
 * ```
 */
export default defineComponent({
  name: "ProjectColumn",
  props,
  setup(props) {
    const projectStore = useProjectStore();

    const project = computed<ProjectListItem | undefined>(() => {
      if (!props.projectId) return undefined;
      return projectStore.projects.find((p) => p.id === props.projectId);
    });

    // Only show in global view (or when forced via prop)
    const shouldShow = computed(() => props.forceShow || projectStore.isGlobalView);

    // Threads cannot be reassigned
    const canReassign = computed(() => {
      return props.resourceType && props.resourceType !== "thread" && props.onReassign;
    });

    return () => {
      if (!shouldShow.value) return null;

      // Case 1: Orphan resource (no project_id)
      if (!props.projectId) {
        return (
          <div class="flex items-center gap-1">
            <Tooltip text={$t("projects.unassigned_tooltip")}>
              <span class="text-xs italic text-muted-foreground">{$t("projects.unassigned")}</span>
            </Tooltip>
            {canReassign.value && (
              <Tooltip text={$t("projects.reassign_project")}>
                <Button
                  size="xs"
                  variant="text"
                  shape="square"
                  icon="lucide:refresh-cw"
                  onClick={() => {
                    import("@/components/ReassignProjectModal").then((m) => {
                      // Type assertion: we only get here when resourceType !== "thread"
                      // because canReassign filters out threads
                      m.openReassignProjectModal({
                        resourceType: props.resourceType as
                          | "agent"
                          | "webhook"
                          | "api_key"
                          | "knowledge"
                          | "structured_analysis",
                        resourceId: props.resourceId!,
                        onReassign: props.onReassign!,
                      });
                    });
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      }

      // Case 2: Project was deleted (project_id exists but project not found in store)
      if (!project.value) {
        return (
          <div class="flex items-center gap-2">
            <span class="font-mono text-xs text-muted-foreground">{formatId(props.projectId)}</span>
            <Badge color="red" size="xs" variant="subtle">
              {$t("projects.removed")}
            </Badge>
          </div>
        );
      }

      // Case 3: Project exists - show badge with icon and color
      const projectName = project.value.name;
      const projectIcon = project.value.settings?.icon ?? "folder";
      const projectColor = project.value.settings?.color ?? "brand";
      const isArchived = project.value.status === "archived";

      if (isArchived) {
        return (
          <div class="flex items-center gap-1.5">
            <div
              class="flex h-5 w-5 items-center justify-center rounded bg-accent-9"
              data-accent={projectColor}
            >
              <Icon icon={resolveProjectIcon(projectIcon)} class="h-3 w-3 text-accent-contrast" />
            </div>
            <span class="whitespace-nowrap text-muted-foreground">{projectName}</span>
            <Badge color="yellow" size="xs" variant="subtle">
              {$t("archived" as any)}
            </Badge>
          </div>
        );
      }

      return (
        <RouterLink
          to={{ name: "ProjectMembers", params: { id: project.value.id } }}
          class="group flex items-center gap-1.5 text-sm hover:underline"
        >
          <div
            class="flex h-5 w-5 items-center justify-center rounded bg-accent-9"
            data-accent={projectColor}
          >
            <Icon
              icon={resolveProjectIcon(projectIcon)}
              class="h-3 w-3 text-accent-contrast"
              data-accent={projectColor}
            />
          </div>
          <span class="whitespace-nowrap group-hover:text-brand-11">{projectName}</span>
        </RouterLink>
      );
    };
  },
});

export type { Props as ProjectColumnProps };
